/*
Origin Core 1.0

called from /blocks/_product-store-availability.liquid

SUMMARY
--------------------
This script defines a custom web component <store-availability-component> that
manages the in-store availability off-canvas panel for Shopify products.

Because the boutique locations are not enabled for fulfilment, Liquid
`store_availabilities` is empty for them. Instead we fetch live stock from an
App Proxy endpoint backed by the ERP integration. The proxy is expected to
respond with:

  { "stockByLocation": { "<shopify_location_id>": true | false, ... } }

When no proxy URL is configured, or the request fails, every store falls back
to "Out of stock" so the panel still renders and the customer can call/visit.

The component listens for `variant:update` so the product summary and stock
state stay in sync as the customer changes variants.
*/

import { ThemeEvents } from '@theme/events';
import { DialogComponent } from '@theme/dialog';

class StoreAvailabilityLayout extends HTMLElement {
  /** @type {MediaQueryList} */
  #wideDesktop = window.matchMedia('(min-width: 80rem)');
  /** @type {HTMLElement | null} */
  #component = null;
  /** @type {Comment | null} */
  #placeholder = null;

  connectedCallback() {
    this.#component ||= this.querySelector('store-availability-component');
    if (!this.#component) return;

    if (!this.#placeholder) {
      this.#placeholder = document.createComment('store-availability-position');
      this.insertBefore(this.#placeholder, this.#component);
    }

    this.#wideDesktop.addEventListener('change', this.#syncPosition);
    this.#syncPosition();
  }

  disconnectedCallback() {
    this.#wideDesktop.removeEventListener('change', this.#syncPosition);
  }

  #syncPosition = () => {
    if (!this.#component || !this.#placeholder) return;

    const productDetails = this.closest('[data-testid="product-information-details"]');
    const desktopHost = productDetails?.querySelector('[data-store-availability-desktop-host]');
    const mobileHost = productDetails?.querySelector('[data-store-availability-mobile-host]');

    if (this.#wideDesktop.matches) {
      if (mobileHost) {
        mobileHost.hidden = true;
        mobileHost.setAttribute('aria-hidden', 'true');
      }

      if (desktopHost) {
        desktopHost.hidden = false;
        desktopHost.removeAttribute('aria-hidden');
        desktopHost.append(this.#component);
        return;
      }
    }

    if (desktopHost) {
      desktopHost.hidden = true;
      desktopHost.setAttribute('aria-hidden', 'true');
    }

    if (mobileHost) {
      mobileHost.hidden = false;
      mobileHost.removeAttribute('aria-hidden');
      mobileHost.append(this.#component);
      return;
    }

    this.#placeholder.parentNode?.insertBefore(this.#component, this.#placeholder.nextSibling);
  };
}

class StoreAvailabilityComponent extends DialogComponent {
  /** @type {AbortController | undefined} */
  #activeFetch;
  /** @type {boolean} */
  #hasFetched = false;
  /** @type {((event: Event) => void) | undefined} */
  #variantUpdateHandler;
  /** @type {Element | null} */
  #closestSection = null;

  connectedCallback() {
    super.connectedCallback();

    this.#closestSection = this.closest('.shopify-section');
    if (this.#closestSection) {
      this.#variantUpdateHandler = (event) => this.#onVariantUpdate(/** @type {CustomEvent} */ (event));
      this.#closestSection.addEventListener(ThemeEvents.variantUpdate, this.#variantUpdateHandler);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.#closestSection && this.#variantUpdateHandler) {
      this.#closestSection.removeEventListener(ThemeEvents.variantUpdate, this.#variantUpdateHandler);
    }
  }

  showDialog() {
    super.showDialog();
    if (!this.#hasFetched) {
      this.#fetchAvailability();
    }
  }

  /**
   * Handles variant updates by refreshing the product summary and re-fetching
   * availability for the new variant. Mirrors `local-pickup.js` in spirit:
   * cancels in-flight requests and only updates UI for the latest variant.
   * @param {CustomEvent} event
   */
  #onVariantUpdate(event) {
    const detail = event.detail || {};
    const resource = detail.resource;
    if (!resource) return;

    if (detail.data && detail.data.newProduct) {
      this.dataset.productUrl = detail.data.newProduct.url;
    }

    if (String(resource.id) === this.dataset.variantId) return;

    this.dataset.variantId = String(resource.id);
    this.#updateProductSummary(resource, detail.data?.html);

    // Reset stock state to loading and re-fetch if the dialog is currently open
    // (or has been opened previously — so a re-open shows fresh data).
    this.#resetStockStates();
    this.#hasFetched = false;
    if (this.refs.dialog && this.refs.dialog.open) {
      this.#fetchAvailability();
    }
  }

  /**
   * Updates the product summary panel inside the dialog using the variant
   * data from the variant:update event. Falls back gracefully when fields
   * aren't present.
   * @param {Object} variant
   * @param {Document} [html]
   */
  #updateProductSummary(variant, html) {
    const { variantTitle, variantPrice, variantSku, productSummary } = this.refs;

    const nextProductSummary =
      html?.querySelector(`store-availability-component[data-section-id="${this.dataset.sectionId}"] [ref="productSummary"]`) ||
      html?.querySelector('store-availability-component [ref="productSummary"]');
    if (productSummary && nextProductSummary) {
      productSummary.innerHTML = nextProductSummary.innerHTML;
      return;
    }

    if (variantPrice && typeof variant.price === 'number') {
      const formatter = (window.Shopify && window.Shopify.formatMoney) || null;
      if (formatter) {
        variantPrice.textContent = formatter(variant.price);
      } else {
        // Variant.price from Shopify variant.js is in cents.
        variantPrice.textContent = (variant.price / 100).toLocaleString(undefined, {
          style: 'currency',
          currency: (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'GBP',
        });
      }
    }

    if (variantSku && variant.sku) {
      const skuLabel = variantSku.dataset.label || variantSku.textContent.split(':')[0] || 'SKU';
      variantSku.textContent = `${skuLabel}: ${variant.sku}`;
    }

    if (variantTitle && variant.title) {
      // Keep any swatch markup that's been server-rendered; only update the
      // trailing text node so the swatch survives variant changes.
      const lastNode = variantTitle.lastChild;
      if (lastNode && lastNode.nodeType === Node.TEXT_NODE) {
        lastNode.textContent = ` ${variant.title}`;
      } else {
        variantTitle.appendChild(document.createTextNode(` ${variant.title}`));
      }
    }

    if (productSummary && variant.featured_media && variant.featured_media.preview_image) {
      const img = productSummary.querySelector('img');
      if (img) {
        img.src = variant.featured_media.preview_image.src;
        img.alt = variant.featured_media.preview_image.alt || img.alt;
      }
    }
  }

  #resetStockStates() {
    const items = this.refs.storeStatus || [];
    for (const status of items) {
      this.#renderStatus(status, 'loading');
    }
  }

  /**
   * Sets the visible stock pill for one store row.
   * @param {HTMLElement} statusEl
   * @param {'loading' | 'in_stock' | 'out_of_stock'} state
   */
  #renderStatus(statusEl, state) {
    if (!statusEl) return;
    statusEl.dataset.state = state;
    const pills = statusEl.querySelectorAll('[data-status-state]');
    for (const pill of pills) {
      if (pill.dataset.statusState === state) {
        pill.removeAttribute('hidden');
      } else {
        pill.setAttribute('hidden', '');
      }
    }
  }

  #createAbortController() {
    if (this.#activeFetch) this.#activeFetch.abort();
    this.#activeFetch = new AbortController();
    return this.#activeFetch;
  }

  async #fetchAvailability() {
    const variantId = this.dataset.variantId;
    const proxyUrl = this.dataset.proxyUrl;
    const locationIds = (this.dataset.locationIds || '').split(',').map((s) => s.trim()).filter(Boolean);

    if (!variantId || !proxyUrl || locationIds.length === 0) {
      this.#applyStockMap({});
      return;
    }

    const abortController = this.#createAbortController();
    const url = new URL(proxyUrl, window.location.origin);
    url.searchParams.set('variant_id', variantId);
    url.searchParams.set('location_ids', locationIds.join(','));

    try {
      const response = await fetch(url.toString(), { signal: abortController.signal });
      if (!response.ok) throw new Error(`Proxy returned ${response.status}`);
      const data = await response.json();
      if (abortController.signal.aborted) return;
      this.#applyStockMap(data.stockByLocation || {});
      this.#hasFetched = true;
    } catch (error) {
      if (abortController.signal.aborted) return;
      // Graceful fallback: render every store as Out of stock so the customer
      // still sees the panel and can call the boutique to confirm.
      this.#applyStockMap({});
      this.#hasFetched = true;
    }
  }

  /**
   * @param {Object<string, boolean>} stockByLocation
   */
  #applyStockMap(stockByLocation) {
    const items = this.refs.storeStatus || [];
    for (const status of items) {
      const storeRow = status.closest('[data-location-id]');
      if (!storeRow) continue;
      const locationId = storeRow.dataset.locationId;
      const inStock = Boolean(locationId && stockByLocation[locationId]);
      this.#renderStatus(status, inStock ? 'in_stock' : 'out_of_stock');
    }
  }
}

if (!customElements.get('store-availability-layout')) {
  customElements.define('store-availability-layout', StoreAvailabilityLayout);
}

if (!customElements.get('store-availability-component')) {
  customElements.define('store-availability-component', StoreAvailabilityComponent);
}
